import { PaginationRequest } from '@classes/pagination/pagination-request';
import { AuthService } from '@services/auth.service';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { tap, map } from 'rxjs/operators';

import { User } from '@classes/user/user';
import { environment } from '@environments/environment';
import { UiService } from './ui.service';
import { RoleName } from '@enums/role-name.enum';
import { Pagination } from '@classes/pagination/pagination';
import { Client } from '@classes/client';

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  usersListChanged = new Subject<User[]>();
  paginationSubject = new BehaviorSubject<Pagination>(null);

  private baseUrl: string = environment.apiUrl + 'users';
  private users: User[];
  private lastQuery: string;
  private getListSub: Subscription;

  actualUser: User;

  private _globalSelectedClient: Client | null = null;

  public get globalSelectedClient(): Client | null {
    return this._globalSelectedClient;
  }

  public globalSelectedClientSubject = new Subject<Client | null>();

  constructor(
    private http: HttpClient,
    private uiSrv: UiService,
    private authSrv: AuthService,
  ) {}

  getList(
    role?: RoleName,
    isActive?: boolean,
    clientId?: string,
    siteId?: string,
    multiRoles?: RoleName[],
  ): Observable<User[]> {
    let httpParams = new HttpParams();

    const filtersQuery = [];

    if (role) {
      filtersQuery.push(`rolename==${role}`);
    }

    if (multiRoles) {
      filtersQuery.push(`rolename==${multiRoles.join('|')}`);
    }

    if (isActive) {
      filtersQuery.push(`isActive==${isActive}`);
    }

    if (clientId) {
      filtersQuery.push(`clientId==${clientId}`);
    }

    if (siteId) {
      filtersQuery.push(`siteId==${siteId}`);
    }

    if (filtersQuery.length > 0)
      httpParams = httpParams.append('Filters', filtersQuery.join());

    return this.http.get<User[]>(this.baseUrl, { params: httpParams }).pipe(
      map((users) => {
        this.users = users.map((u) => User.fromObject(u));
        this.users.sort((a, b) => a.username.localeCompare(b.username));
        this.usersListChanged.next([...this.users]);
        return [...this.users];
      }),
    );
  }

  getListWithPagination(
    request: PaginationRequest,
    role?: RoleName,
    isActive?: boolean,
    clientId?: string,
    siteId?: string,
  ): void {
    if (this.getListSub) {
      this.getListSub.unsubscribe();
    }

    let httpParams = new HttpParams();

    if (request.pageSize && request.page) {
      httpParams = httpParams
        .append('pageSize', request.pageSize.toString())
        .append(
          'page',
          request.query !== this.lastQuery ? '1' : request.page.toString(),
        );
    }

    if (request.sort) {
      if (request.sort.includes('role')) {
        request.sort = request.sort.replace('role', 'roleName');
      }

      httpParams = httpParams.append('sorts', request.sort);
    }

    const filtersQuery = [];

    if (request.query) {
      const filters = request.query;
      filtersQuery.push(`(username|email|roleName|createdBy)@=${filters}`);
    }

    if (role) {
      filtersQuery.push(`roleName==${role}`);
    }

    if (isActive) {
      filtersQuery.push(`isActive==${isActive}`);
    }

    if (clientId) {
      filtersQuery.push(`clientId==${clientId}`);
    }

    if (siteId) {
      filtersQuery.push(`siteId==${siteId}`);
    }

    if (filtersQuery.length > 0)
      httpParams = httpParams.append('Filters', filtersQuery.join());

    this.getListSub = this.http
      .get(this.baseUrl, { params: httpParams, observe: 'response' })
      .subscribe((result: HttpResponse<User[]>) => {
        this.uiSrv.loadingStateChanged.next(false);
        const pagination: Pagination = JSON.parse(
          result.headers.get('X-Pagination'),
        );
        this.users = result.body.map((u) => User.fromObject(u));
        this.users.sort((a, b) => a.username.localeCompare(b.username));
        this.usersListChanged.next([...this.users]);
        this.paginationSubject.next({ ...pagination });
      });

    this.lastQuery = request.query;
  }

  getUsersLastTimeRecords() {
    // /users/last-time-record-in
    return this.http.get<User[]>(`${this.baseUrl}/last-time-record-in`).pipe(
      map((users) => {
        return users
          .map((u) => User.fromObject(u))
          .sort((a, b) => a.username.localeCompare(b.username));
      }),
    );
  }

  getOneUser(id: string): Observable<User> {
    return this.http.get<User>(this.baseUrl).pipe(
      map((user) => {
        return user;
      }),
    );
  }

  put(user: User): Observable<User> {
    return this.http.put<User>(this.baseUrl, user).pipe(
      tap((editedUser) => {
        editedUser = User.fromObject(editedUser);
        this.users = this.users.map((u) =>
          u.id === editedUser.id ? editedUser : u,
        );
        this.usersListChanged.next([...this.users]);
      }),
    );
  }

  post(user: User): Observable<User> {
    return this.http.post<User>(this.baseUrl, user).pipe(
      tap((createdUser) => {
        createdUser = User.fromObject(createdUser);
        this.users.push(createdUser);
        this.usersListChanged.next([...this.users]);
        this.showEmailSentMsg();
      }),
    );
  }

  getListBySiteIds(
    siteIds: string[],
    role?: RoleName,
    isActive?: boolean,
  ): Observable<User[]> {
    const filtersQuery = [];

    filtersQuery.push(`siteId==${siteIds.join('|')}`);

    if (role) {
      filtersQuery.push(`rolename==${role}`);
    }

    if (isActive) {
      filtersQuery.push(`isActive==${isActive}`);
    }

    return this.http
      .post<User[]>(this.baseUrl + '/filters', { filters: filtersQuery.join() })
      .pipe(
        map((users) => {
          this.users = users.map((u) => User.fromObject(u));
          this.usersListChanged.next([...this.users]);
          return [...this.users];
        }),
      );
  }

  delete(id: string): Observable<void> {
    return this.http.get<void>(`${this.baseUrl}/${id}`).pipe(
      tap(() => {
        this.users = this.users.filter((u) => u.id !== id);
        this.usersListChanged.next([...this.users]);
      }),
    );
  }

  updateUsersListSubject(editedUser: User) {
    editedUser = User.fromObject(editedUser);
    this.users = this.users.map((u) =>
      u.id === editedUser.id ? editedUser : u,
    );
    this.usersListChanged.next([...this.users]);
  }

  changeStatus(
    id: string,
    method: 'activate' | 'deactivate',
  ): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${id}/${method}`, null);
  }

  private showEmailSentMsg(isEmailSent = true): void {
    if (isEmailSent) {
      this.uiSrv.showSnackbar('users.emailSent', true, 5000);
    }
  }

  sendApkMail(userId: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${userId}/send-app`, null);
  }

  public getListByClientIds(
    clientIds: string[],
    role?: RoleName,
    isActive?: boolean,
  ): Observable<User[]> {
    let httpParams = new HttpParams();
    const filtersQuery = [];

    filtersQuery.push(`clientId==${clientIds.join('|')}`);

    if (role) filtersQuery.push(`rolename==${role}`);

    if (isActive) filtersQuery.push(`isActive==${isActive}`);

    httpParams = httpParams.append('filters', filtersQuery.join());

    return this.http
      .get<User[]>(`${this.baseUrl}`, { params: httpParams })
      .pipe(
        map((users) => {
          this.users = users.map((u) => User.fromObject(u));
          this.usersListChanged.next([...this.users]);
          return [...this.users];
        }),
      );
  }

  public patchClient$(client: Client) {
    this._globalSelectedClient = client;
    return this.http.patch(`${this.baseUrl}/client/${client.id}`, null).pipe(
      tap(() => {
        this.globalSelectedClientSubject.next(client);
      }),
    );
  }

  public patchClientAll$() {
    this._globalSelectedClient = null;
    return this.http.patch(`${this.baseUrl}/client/all`, null).pipe(
      tap(() => {
        this.globalSelectedClientSubject.next(null);
      }),
    );
  }

  public getCurrentUser$(): Observable<User> {
    return this.http.get<User>(`${this.baseUrl}/current`);
  }
}
